import { useEffect, useState, useCallback, useContext } from "react";
import { CiSearch, CiCirclePlus } from "react-icons/ci";
import { MdEdit, MdDelete, MdLockReset, MdVisibility } from "react-icons/md";
import { LiaWalletSolid } from "react-icons/lia";
import toast from "react-hot-toast";
import "../../styles/adminUsers.css";
import "../../styles/subAdmin.css";
import Loader from "../../components/Loader";
import NoData from "../../components/NoData";
import ConfirmDeleteModal from "../../components/ConfirmDeleteModal";
import ResetPasswordModal from "../../components/ResetPasswordModal";
import { AuthContext } from "../../context/AuthContext";
import SubAdminServices from "../../services/SubAdminServices";
import PasswordServices from "../../services/PasswordServices";
import { getAvatarColor, getInitials } from "../../utils/utils";
import SubAdminFormModal from "./modals/SubAdminFormModal";
import SubAdminWalletModal from "./modals/SubAdminWalletModal";
import SubAdminDetailModal from "./modals/SubAdminDetailModal";

/**
 * Sub admins under the logged-in Admin. Super Admin gets the same list
 * across every admin (with the owning admin shown) but view-only: the
 * sub admin is the Admin's to run.
 */
const SubAdminManage = () => {
  const api = new SubAdminServices();
  const passwordApi = new PasswordServices();
  const { user } = useContext(AuthContext);
  const isSuperAdmin = Number(user?.role_id) === 1;
  const canManage = Number(user?.role_id) === 2;
  // Super Admin can top up / pull back a sub admin's wallet directly,
  // even though everything else about the sub admin is the Admin's.
  const canWallet = canManage || isSuperAdmin;

  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [searchFilter, setSearchFilter] = useState("");
  const [pagination, setPagination] = useState(null);

  const [formOpen, setFormOpen] = useState(false);
  const [editData, setEditData] = useState(null);
  const [walletOpen, setWalletOpen] = useState(false);
  const [walletUser, setWalletUser] = useState(null);
  const [detailId, setDetailId] = useState(null);
  const [deleteUser, setDeleteUser] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [resetUser, setResetUser] = useState(null);
  const [resetLoading, setResetLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.list(page, limit, searchFilter);
      if (response.isSuccess) {
        setRows(response.data || []);
        setPagination(response.pagination);
      } else {
        toast.error(response.message);
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to load sub admins");
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, limit, searchFilter]);

  useEffect(() => {
    load();
  }, [load]);

  const toggleStatus = async (row) => {
    const next = row.is_active === 1 ? 0 : 1;
    try {
      const response = await api.setStatus(row.id, next);
      if (response.isSuccess) {
        toast.success(response.message);
        setRows((prev) => prev.map((u) => (u.id === row.id ? { ...u, is_active: next } : u)));
      } else {
        toast.error(response.message);
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to update status");
    }
  };

  const handleDelete = async () => {
    if (!deleteUser) return;
    setDeleteLoading(true);
    try {
      const response = await api.remove(deleteUser.id);
      if (response.isSuccess) {
        toast.success(response.message);
        load();
      } else {
        toast.error(response.message);
      }
    } catch (error) {
      toast.dismiss();
      toast.error(error?.response?.data?.message || "Failed to delete sub admin");
    } finally {
      setDeleteLoading(false);
      setDeleteUser(null);
    }
  };

  const handleResetPassword = async (newPassword) => {
    if (!resetUser) return;
    setResetLoading(true);
    try {
      const response = await passwordApi.resetUserPassword({
        userId: resetUser.id,
        newPassword,
        description: `Admin password reset for sub admin ${resetUser.username}`,
      });
      if (response.isSuccess) {
        toast.success(response.message || "Password reset successfully");
        setResetUser(null);
      } else {
        toast.error(response.message || "Failed to reset password");
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to reset password");
    } finally {
      setResetLoading(false);
    }
  };

  const Avatar = ({ name, image }) =>
    image ? (
      <img src={image} alt={name} className="admin-avatar" />
    ) : (
      <div className="admin-avatar fallback-avatar" style={{ backgroundColor: getAvatarColor(name) }}>
        {getInitials(name)}
      </div>
    );

  return (
    <div className="admin-manage-view">
      <div className="admin_user-header">
        <div className="admin-header-row">
          <div className="admin-search">
            <div className="search-box">
              <CiSearch size={18} className="search-icon" />
              <input
                type="text"
                className="form-control search-input"
                placeholder="Search sub admin"
                value={searchFilter}
                onChange={(e) => {
                  setPage(1);
                  setSearchFilter(e.target.value);
                }}
              />
            </div>
          </div>

          {canManage && (
            <button
              className="add_admin-btn"
              onClick={() => {
                setEditData(null);
                setFormOpen(true);
              }}
            >
              <CiCirclePlus size={18} />
              Add Sub Admin
            </button>
          )}
        </div>
        {isSuperAdmin && (
          <p className="sub-admin-hint">
            Sub admins are created and managed by their Admin. You can view details and adjust wallets here.
          </p>
        )}
      </div>

      <div>
        {loading ? (
          <Loader message="Loading sub admins..." />
        ) : rows.length === 0 ? (
          <NoData
            message="No Sub Admins Found"
            subText={
              canManage
                ? "Add a sub admin to let them run their own outlets and mobile users."
                : "No admin has created a sub admin yet."
            }
          />
        ) : (
          <>
            <div className="admin-table-header sub-admin-table-header">
              <div className="col-user">User</div>
              <div className="col-mobile">Mobile</div>
              <div className="col-role">{isSuperAdmin ? "Admin" : "Users"}</div>
              <div className="col-status">Status</div>
              <div className="col-action">Action</div>
            </div>

            <div className="admin-table">
              {rows.map((row) => (
                <div className="admin-row" key={row.id}>
                  <div className="admin-user">
                    <Avatar name={row.name} image={row.profile_image} />
                    <div>
                      <div className="admin-name">{row.name}</div>
                      <div className="admin-username">({row.username})</div>
                      <div className="admin-username">
                        Wallet balance: <span className="wallet">{row.wallet_balance ?? 0}</span>
                      </div>
                    </div>
                  </div>

                  <div className="admin-row-meta">
                    <div className="admin-col">
                      <div className="admin-username">{row.mobile}</div>
                    </div>

                    <div className="admin-col">
                      {isSuperAdmin ? (
                        <span className="badge badge-basic" title="Owning admin">
                          {row.admin_name || row.admin_username || "—"}
                        </span>
                      ) : (
                        <span className="sub-admin-counts" title="Outlets · Mobile users">
                          {row.outlet_count} outlets · {row.mobile_count} mobile
                        </span>
                      )}
                    </div>

                    <div className="admin-col">
                      <label className={`switch ${canManage ? "" : "switch-readonly"}`}>
                        <input
                          type="checkbox"
                          checked={row.is_active === 1}
                          disabled={!canManage}
                          onChange={() => toggleStatus(row)}
                        />
                        <span className="slider round"></span>
                      </label>
                    </div>
                  </div>

                  <div className="admin-actions">
                    <button className="icon-btn edit" title="View details" onClick={() => setDetailId(row.id)}>
                      <MdVisibility size={18} />
                    </button>
                    {canWallet && (
                      <button
                        className="icon-btn edit"
                        title="Wallet add / deduct"
                        onClick={() => {
                          setWalletUser(row);
                          setWalletOpen(true);
                        }}
                      >
                        <LiaWalletSolid size={18} />
                      </button>
                    )}
                    {canManage && (
                      <>
                        <button
                          className="icon-btn edit"
                          title="Edit"
                          onClick={() => {
                            setEditData(row);
                            setFormOpen(true);
                          }}
                        >
                          <MdEdit size={18} />
                        </button>
                        <button className="icon-btn edit" title="Reset password" onClick={() => setResetUser(row)}>
                          <MdLockReset size={18} />
                        </button>
                        <button className="icon-btn delete" title="Delete" onClick={() => setDeleteUser(row)}>
                          <MdDelete size={18} />
                        </button>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {pagination && pagination.totalPages > 1 && (
              <div className="modern-pagination">
                <div className="mp-limit">
                  <span>Show</span>
                  <select value={limit} onChange={(e) => setLimit(Number(e.target.value))}>
                    <option value="10">10</option>
                    <option value="20">20</option>
                    <option value="50">50</option>
                    <option value="100">100</option>
                  </select>
                </div>
                <div className="mp-controls">
                  <button disabled={page === 1} onClick={() => setPage(page - 1)}>
                    Prev
                  </button>
                  <div className="mp-info">
                    Page <strong>{pagination.page}</strong> of <strong>{pagination.totalPages}</strong>
                  </div>
                  <button disabled={page === pagination.totalPages} onClick={() => setPage(page + 1)}>
                    Next
                  </button>
                </div>
                <div className="mp-jump">
                  <select value={page} onChange={(e) => setPage(Number(e.target.value))}>
                    {Array.from({ length: pagination.totalPages }, (_, i) => (
                      <option key={i + 1} value={i + 1}>
                        Page {i + 1}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      <SubAdminFormModal
        show={formOpen}
        onClose={() => {
          setFormOpen(false);
          setEditData(null);
        }}
        editData={editData}
        onSaved={load}
      />

      <SubAdminWalletModal
        show={walletOpen}
        onClose={() => {
          setWalletOpen(false);
          setWalletUser(null);
        }}
        walletUser={walletUser}
        onSaved={(id, balance) =>
          setRows((prev) => prev.map((u) => (u.id === id ? { ...u, wallet_balance: balance } : u)))
        }
      />

      <SubAdminDetailModal show={detailId != null} onClose={() => setDetailId(null)} subAdminId={detailId} />

      <ConfirmDeleteModal
        show={deleteUser != null}
        onClose={() => setDeleteUser(null)}
        onConfirm={handleDelete}
        title="Delete Sub Admin"
        message={`Delete "${deleteUser?.name}"? Their wallet balance returns to you. A sub admin who still has outlets or mobile users cannot be deleted.`}
        loading={deleteLoading}
      />

      <ResetPasswordModal
        show={resetUser != null}
        onClose={() => {
          if (resetLoading) return;
          setResetUser(null);
        }}
        onConfirm={handleResetPassword}
        userName={resetUser?.name}
        loading={resetLoading}
        title="Reset Sub Admin Password"
      />
    </div>
  );
};

export default SubAdminManage;
